import { useApolloClient } from "@apollo/client";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import itemsInventorygql from "gql/dashboard/sales/itemMode/itemsInventory.gql";
import Item from "./item/Item";
import { X } from "react-bootstrap-icons";
import InfiniteScroll from "react-infinite-scroll-component";
import { useAppContext } from "contexts/AppContext";
import { useFormContext } from "contexts/FormContext";

const ItemView = ({ levels, baseFilters, setBaseFilters, group = null }) => {
  // contexts
  const { appState } = useAppContext();
  const { formState, setFormState } = useFormContext();

  // states
  const [initialItems, setInitialItems] = useState([]);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ ...baseFilters, search: "" });
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({
    totalPages: 0,
    totalRecords: 0,
  });

  const searchTopOffset =
    document.getElementById("topbar-container").offsetHeight;

  // hooks
  const apollo = useApolloClient();

  // functions
  const next = () => {
    setCurrentPage(currentPage + 1);
    setItems((items) => [
      ...items,
      ...initialItems.slice(currentPage * 20, (currentPage + 1) * 20),
    ]);
    setHasMore(pageInfo.totalPages !== currentPage + 1);
  };

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    setBaseFilters(newFilters);
  };

  const buildData = (filters, levels) => {
    // Preparing filter ingridients
    const tags = filters.tags.map((x) => x.value);
    const excludedTags = filters.excludedTags.map((x) => x.value);

    // Apply filters
    const initialItems = appState.resources.salesItems.filter((item) => {
      // searching
      if (filters.search) {
        const vals = filters.search.toLowerCase().split(/\s*[,.]+\s*|\s+/);
        const sina = item.displayName.toLowerCase();
        if (!vals.every((v) => sina.includes(v))) return false;
      }

      // group filtering
      const groupFilter = filterGroup(item, filters);

      // tag filtering
      const tagIncludedFilter = tags.length
        ? item.tags.some((tag) => tags.includes(tag))
        : true;
      const tagExcludedFilter = excludedTags.length
        ? !item.tags.some((tag) => excludedTags.includes(tag))
        : true;

      // available filtering
      const availableFilter =
        filters.onlyAvailables && levels ? levels[item.id] > 0 : true;

      // photo filtering
      const photoFilter = filters.withPhoto ? !!item.image : true;

      // price filtering
      let priceFilter = filters.priceFrom
        ? item.suggestedSalesPrice >= filters.priceFrom
        : true;
      priceFilter = filters.priceTo
        ? priceFilter && item.suggestedSalesPrice <= filters.priceTo
        : priceFilter;

      return (
        groupFilter &&
        tagIncludedFilter &&
        tagExcludedFilter &&
        availableFilter &&
        photoFilter &&
        priceFilter
      );
    });

    // Apply sorting
    const sort = [
      {
        key: "displayName",
        position: filters.sortBy.displayName.position,
        direction: filters.sortBy.displayName.direction,
      },
      {
        key: "itemNumber",
        position: filters.sortBy.itemNumber.position,
        direction: filters.sortBy.itemNumber.direction,
      },
      {
        key: "itemGroup.name",
        position: filters.sortBy.groupName.position,
        direction: filters.sortBy.groupName.direction,
      },
      {
        key: "suggestedSalesPrice",
        position: filters.sortBy.itemPrice.position,
        direction: filters.sortBy.itemPrice.direction,
        type: "number",
      },
      {
        key: "createdAt",
        position: filters.sortBy.itemCreatedAt.position,
        direction: filters.sortBy.itemCreatedAt.direction,
        type: "date",
      },
    ]
      .filter((x) => x.direction && x.position)
      .sort((a, b) => (a.position > b.position ? 1 : -1));

    initialItems.sort((a, b) => {
      if (sort.length) {
        for (const sortBy of sort) {
          const aValue = getValue(a, sortBy.key);
          const bValue = getValue(b, sortBy.key);

          const result = compareValues(aValue, bValue, sortBy.type);
          if (result !== 0)
            return result * (sortBy.direction === "asc" ? 1 : -1);
        }
        return 0;
      } else {
        let check = compareValues(a.itemGroup?.name, b.itemGroup?.name);
        if (check === 0) check = compareValues(a.displayName, b.displayName);
        return check;
      }
    });

    setPageInfo({
      totalPages: Math.ceil(initialItems.length / 20),
      totalRecords: initialItems.length,
    });
    setCurrentPage(1);
    window.scrollTo(0, 0);
    setItems(initialItems.slice(0, 20));
    setInitialItems(initialItems);
    setHasMore(initialItems.length > 20);
  };

  const filterGroup = (item, filters) => {
    // Preparing filter ingridients
    const includedItemGroups = group
      ? [group]
      : filters.itemGroups.map((x) => x.value);
    const excludedItemGroups = group
      ? []
      : filters.excludedItemGroups.map((x) => x.value);

    // Inclusion filter
    let l1 = includedItemGroups.length
      ? includedItemGroups.some((group) => group.id == item.itemGroup?.id)
      : true;
    if (!l1 && item.itemGroup?.parentGroup) {
      // parent checking
      let parent = getParentGroup(item.itemGroup);
      while (parent && !l1) {
        l1 = includedItemGroups.some((group) => group.id == parent.id);
        parent = getParentGroup(parent);
      }
    }

    // Exclusion filter
    let l2 = excludedItemGroups.length
      ? !excludedItemGroups.some((group) => group.id == item.itemGroup?.id)
      : true;
    if (l2 && item.itemGroup?.parentGroup) {
      // parent checking
      let parent = getParentGroup(item.itemGroup);
      while (parent && l2) {
        l2 = !excludedItemGroups.some((group) => group.id == parent.id);
        parent = getParentGroup(parent);
      }
    }

    return l1 && l2;
  };

  const getParentGroup = (group) => {
    return appState.resources.itemGroups.find(
      (g) => g.id == group?.parentGroup?.id
    );
  };

  const getValue = (object, key) => {
    if (!object) return null;

    const keyChain = key.split(".");
    let result = object;
    for (const _k of keyChain)
      if (result && result.hasOwnProperty(_k)) result = result[_k];
      else return null;

    return result;
  };

  const compareValues = (a, b, type = "text") => {
    if ((a ?? b) === undefined) return 0; // if both are null or undefined
    if (a === null || a === undefined) return 1;
    if (b === null || b === undefined) return -1;

    if (!type || type === "text")
      return a.toLowerCase().localeCompare(b.toLowerCase());
    else if (type === "number") return a > b ? 1 : a < b ? -1 : 0;
    else if (type === "date") return a > b ? 1 : a < b ? -1 : 0;
  };

  // effects
  useEffect(() => {
    if (appState.settings.internet.online && items.length) {
      const ids = items.reduce((pre, cur) => {
        if (!formState.levelHistory?.hasOwnProperty(cur.id)) pre.push(cur.id);
        return pre;
      }, []);

      // Preventing to fetch all items when ids is an empty array
      if (!ids.length) return;

      setFormState({
        key: "levelLoading",
        value: true,
      });
      apollo
        .query({
          query: itemsInventorygql,
          variables: { items: ids.join() },
        })
        .then((data) => {
          const newHist = data.data.inventoryLevels.reduce(
            (pre, cur) => ({ ...pre, [cur.item]: cur.available }),
            {}
          );
          setFormState({
            key: "levelHistory",
            value: {
              ...(formState.levelHistory || {}),
              ...newHist,
            },
          });
        })
        .catch((e) => { })
        .finally((f) =>
          setFormState({
            key: "levelLoading",
            value: false,
          })
        );
    }
  }, [items]);

  useEffect(() => {
    applyFilters(filters, levels);
  }, [appState.resources.salesItems]);

  useEffect(() => {
    buildData(
      {
        ...baseFilters,
        onlyAvailables: levels ? baseFilters.onlyAvailables : false,
      },
      levels
    );
  }, [baseFilters, levels]);

  return (
    <React.Fragment>
      <Container fluid className="my-2">
        <Row
          className="position-sticky"
          style={{
            zIndex: 9,
            backgroundColor: "#f3f3f4",
            top: `${searchTopOffset}px`,
          }}
        >
          <InputGroup className="mb-3 mt-2 flex-nowrap">
            {/* <Button variant="dark" className="px-3" onClick={() => setShowFilters(true)}><Funnel size={20} /></Button> */}
            <Form.Control
              value={filters.search}
              onChange={(e) =>
                applyFilters({ ...baseFilters, search: e.target.value })
              }
              placeholder="Search..."
              className="w-auto"
              style={{ height: "50px", fontSize: "20px" }}
            />
            <Button
              variant="light"
              className="border bg-white"
              onClick={() => applyFilters({ ...baseFilters, search: "" })}
            >
              <X size={30} />
            </Button>
          </InputGroup>
        </Row>

        {!!items.length && (
          <InfiniteScroll
            dataLength={items.length}
            hasChildren
            children={items}
            next={next}
            scrollThreshold={0.95}
            className="overflow-hidden text-center"
            hasMore={hasMore}
            endMessage={
              <span className="text-muted fw-bold">No more data</span>
            }
            loader={<Spinner className="fa-spin" />}
          >
            <Row className="my-2">
              {items.map((item) => (
                <Col key={item.id} sm={12} md={4} lg={3} className="my-2">
                  <Item item={item} />
                </Col>
              ))}
            </Row>
          </InfiniteScroll>
        )}
      </Container>
    </React.Fragment>
  );
};

export default ItemView;
